import React, { useState, useEffect, useCallback } from 'react';
import './AchievementsMenu.css';
import { X, Award, Star, Trophy, Zap, Target, Lock, CheckCircle2, Flame, Crown } from 'lucide-react';
import { useAuth } from '../utils/AuthContext';
import { useAudio } from '../utils/AudioContext';
import { 
    playUiHoverSound, 
    playUiClickSound, 
    playUiOpenSound, 
    playUiCloseSound 
} from '../utils/sounds';

// Define the achievements and their unlock conditions based on your userData
const ACHIEVEMENTS_LIST = [
    { 
        id: 'first_game', title: 'Initiation', desc: 'Play your first game.', 
        icon: <Target size={28} />, color: '#3b82f6', 
        check: (user) => (user?.gamesPlayed || 0) > 0 
    },
    { 
        id: 'score_1000', title: 'Rookie', desc: 'Score over 1,000 points.', 
        icon: <Star size={28} />, color: '#10b981', 
        check: (user) => (user?.highscore?.alltime?.score || 0) >= 1000 
    },
    { 
        id: 'score_5000', title: 'Pro Player', desc: 'Score over 5,000 points.', 
        icon: <Zap size={28} />, color: '#f59e0b', 
        check: (user) => (user?.highscore?.alltime?.score || 0) >= 5000 
    },
    { 
        id: 'score_10000', title: 'Tile Master', desc: 'Score over 10,000 points.', 
        icon: <Flame size={28} />, color: '#ef4444', 
        check: (user) => (user?.highscore?.alltime?.score || 0) >= 10000 
    },
    { 
        id: 'rich', title: 'Banker', desc: 'Accumulate 1,000 coins in your wallet.', 
        icon: <Award size={28} />, color: '#8b5cf6', 
        check: (user) => (user?.money || 0) >= 1000 
    },
    { 
        id: 'legend', title: 'Living Legend', desc: 'Score over 50,000 points.', 
        icon: <Crown size={28} />, color: '#facc15', 
        check: (user) => (user?.highscore?.alltime?.score || 0) >= 50000 
    }
];

function AchievementsMenu({ onClose }) {
    const { userData } = useAuth();
    const { sfxMuted } = useAudio();
    const [isExiting, setIsExiting] = useState(false);

    // Play Open Sound on Mount
    useEffect(() => {
        playUiOpenSound(sfxMuted);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleClose = useCallback(() => {
        playUiCloseSound(sfxMuted);
        setIsExiting(true);
    }, [sfxMuted]);

    const handleAnimationEnd = (e) => {
        if (e.target !== e.currentTarget) return;
        if (isExiting && onClose) onClose();
    };

    // Calculate Progress
    const unlockedCount = ACHIEVEMENTS_LIST.filter(ach => ach.check(userData)).length;
    const progressPercent = Math.round((unlockedCount / ACHIEVEMENTS_LIST.length) * 100);

    return (
        <div 
            className={`achievements-overlay ${isExiting ? 'fade-out' : 'fade-in'}`}
            style={{ pointerEvents: isExiting ? 'none' : 'auto' }}
        >
            <div 
                className={`achievements-container ${isExiting ? 'pop-out' : ''}`}
                onAnimationEnd={handleAnimationEnd}
            >
                <div className="achievements-header">
                    <div className="header-title-group">
                        <Trophy className="c-yellow" size={32} />
                        <h1>ACHIEVEMENTS</h1>
                    </div>
                    <button 
                        className="close-btn" 
                        onClick={handleClose} 
                        onMouseEnter={() => playUiHoverSound(sfxMuted)}
                    >
                        <X size={24}/>
                    </button>
                </div>

                {/* Progress Bar Area */}
                <div className="achievements-progress-section">
                    <div className="progress-info">
                        <span className="progress-label">Total Completed</span>
                        <span className="progress-stats">{unlockedCount} / {ACHIEVEMENTS_LIST.length}</span>
                    </div>
                    <div className="progress-bar-bg">
                        <div 
                            className="progress-bar-fill" 
                            style={{ width: `${progressPercent}%` }}
                        ></div>
                    </div>
                </div>

                {/* Achievements List */}
                <div className="achievements-content-area custom-scroll">
                    <div className="achievements-list">
                        {ACHIEVEMENTS_LIST.map((ach) => {
                            const isUnlocked = ach.check(userData);

                            return (
                                <div 
                                    key={ach.id} 
                                    className={`achievement-card ${isUnlocked ? 'unlocked' : 'locked'}`}
                                    onMouseEnter={() => playUiHoverSound(sfxMuted)}
                                    onClick={() => playUiClickSound(sfxMuted)}
                                >
                                    <div 
                                        className="ach-icon-wrapper"
                                        style={{ 
                                            borderColor: isUnlocked ? ach.color : 'rgba(255,255,255,0.1)',
                                            color: isUnlocked ? ach.color : '#71717a',
                                            boxShadow: isUnlocked ? `0 0 15px ${ach.color}40` : 'none'
                                        }}
                                    >
                                        {isUnlocked ? ach.icon : <Lock size={28} />}
                                    </div>
                                    
                                    <div className="ach-info">
                                        <h3 style={{ color: isUnlocked ? '#fff' : '#a1a1aa' }}>
                                            {ach.title}
                                        </h3>
                                        <p>{ach.desc}</p>
                                    </div>

                                    <div className="ach-status">
                                        {isUnlocked ? (
                                            <div className="status-badge unlocked">
                                                <CheckCircle2 size={16} /> UNLOCKED
                                            </div>
                                        ) : (
                                            <div className="status-badge locked">
                                                LOCKED
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AchievementsMenu;